"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { formatToIndianCurrency } from "@/app/product/utils/currency.utils";
import type { CalculatorRow, CalculatorTab, LandingCalculator } from "./types";

interface SavingsCalculatorProps {
	data: LandingCalculator;
	activeTab: CalculatorTab;
}

const formatAmount = (amount: number) => {
	const abs = Math.abs(amount);
	const sign = amount < 0 ? "-" : "";
	return `${sign}₹${formatToIndianCurrency(abs.toString())}`;
};

const LEFT_COL =
	"relative w-12 md:w-14 flex-shrink-0 flex items-center justify-center";

const RowOperator: React.FC<{ operator?: "+" | "="; type: string }> = ({
	operator,
	type,
}) => {
	if (!operator) return null;
	const isTotal = type === "total";
	return (
		<div
			className={cn(
				"relative z-20 w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-full text-xs md:text-sm font-medium leading-none border pb-[2px]",
				isTotal
					? "bg-[var(--colors-automint-neon-500)] border-[var(--colors-automint-neon-500)] text-automint-black"
					: "bg-white border-[var(--automint-onboarding-dotted-line)] text-automint-black",
			)}
		>
			{operator}
		</div>
	);
};

const ProductPriceRow: React.FC<{
	row: CalculatorRow;
	tab: CalculatorTab;
}> = ({ row, tab }) => {
	return (
		<div className="flex items-stretch">
			<div className="flex items-center gap-3 px-3 py-1.5 md:px-4 md:py-2 flex-1 min-w-0">
				<div className={`${LEFT_COL} h-12`}>
					{/* eslint-disable-next-line @next/next/no-img-element */}
					<img
						src={tab.product.image}
						alt={tab.product.imageAlt}
						className="relative z-20 max-h-full max-w-full object-contain"
					/>
				</div>
				<div className="flex-1 min-w-0">
					<p className="text-[13px] md:text-sm text-automint-black leading-tight">
						{tab.product.name}
					</p>
					{(tab.product.variant || tab.product.subtext) && (
						<p className="text-[11px] md:text-xs text-text-gray font-light leading-none mt-1 md:-mt-0.5">
							{tab.product.variant || tab.product.subtext}
						</p>
					)}
				</div>
			</div>
			<div className="flex flex-col items-end justify-center px-3 py-1.5 md:px-4 md:py-2 whitespace-nowrap">
				{row.label && (
					<p className="text-[10px] md:text-xs text-text-gray font-light mb-0.5 leading-tight">
						{row.label}
					</p>
				)}
				{row.originalAmount !== undefined && (
					<p className="text-[10px] md:text-xs text-text-gray line-through leading-tight">
						{formatAmount(row.originalAmount)}
					</p>
				)}
				<p className="text-xs md:text-sm  text-automint-black leading-tight">
					{formatAmount(row.amount)}
				</p>
			</div>
		</div>
	);
};

const DiscountRow: React.FC<{ row: CalculatorRow }> = ({ row }) => {
	return (
		<div className="flex items-stretch flex-1">
			<div className="relative z-10 flex items-center gap-3 px-3 py-1.5 md:px-4 md:py-2 flex-1 min-w-0">
				<div className={LEFT_COL}>
					<RowOperator operator={row.operator} type={row.type} />
				</div>
				<div className="flex-1 min-w-0">
					<p className="text-[13px] md:text-sm text-automint-black leading-tight">
						{row.label}
					</p>
					{row.sublabel && (
						<p className="text-[11px] md:text-xs text-text-gray font-light leading-none mt-1 md:-mt-0.5">
							{row.sublabel}
						</p>
					)}
				</div>
			</div>
			<div className="relative z-10 flex flex-col items-end justify-center gap-0.5 px-3 py-1.5 md:px-4 md:py-2 whitespace-nowrap">
				{row.originalAmount !== undefined && (
					<p className="text-[10px] md:text-xs text-text-gray line-through leading-tight">
						{formatAmount(row.originalAmount)}
					</p>
				)}
				<p className="text-[13px] md:text-sm text-automint-black leading-tight">
					{formatAmount(row.amount)}
				</p>
			</div>
		</div>
	);
};

const TotalRow: React.FC<{ row: CalculatorRow }> = ({ row }) => {
	return (
		<div className="flex items-stretch flex-1">
			<div className="relative z-10 flex items-center gap-3 px-3 py-1.5 md:px-4 md:py-2 flex-1 min-w-0">
				<div className={LEFT_COL}>
					<RowOperator operator={row.operator} type={row.type} />
				</div>
				<div className="flex-1 min-w-0">
					<p className="text-[13px] md:text-sm text-automint-black leading-tight">
						{row.label}
					</p>
					{row.sublabel && (
						<p className="text-[11px] md:text-xs text-text-gray font-light leading-none mt-1 md:-mt-0.5">
							{row.sublabel}
						</p>
					)}
				</div>
			</div>
			<div className="relative z-10 flex flex-col items-end justify-center px-3 py-1.5 md:px-4 md:py-2 whitespace-nowrap">
				<p className="text-[13px] md:text-sm text-automint-black leading-tight">
					{formatAmount(row.amount)}
				</p>
				{row.savedAmount !== undefined && row.savedPercent !== undefined && (
					<p className="text-[10px] md:text-xs text-[var(--automint-success-text-light)] mt-0.5 leading-tight">
						Saved {formatAmount(row.savedAmount)} ({row.savedPercent}%)
					</p>
				)}
			</div>
		</div>
	);
};

export const SavingsCalculator: React.FC<SavingsCalculatorProps> = ({
	data,
	activeTab,
}) => {
	const basePrice = Math.abs(
		activeTab.rows.find((r) => r.type === "product-price")?.amount ?? 1,
	);

	return (
		<div className="w-full">
			<div className="relative w-full flex flex-col">
				<div
					className="pointer-events-none absolute left-[36px] md:left-[44px] top-5 md:top-6 bottom-5 md:bottom-6 border-l border-dashed border-[var(--automint-onboarding-dotted-line)] z-10"
					aria-hidden="true"
				/>
				{activeTab.rows.map((row, idx) => {
					const jsonPercent = (row as CalculatorRow & { barPercent?: number })
						.barPercent;
					let barPercent: number;
					if (jsonPercent != null) {
						barPercent = jsonPercent;
					} else if (row.type === "total") {
						barPercent = row.savedPercent ?? 0;
					} else if (row.type === "discount") {
						const savings =
							row.amount < 0
								? Math.abs(row.amount)
								: Math.abs((row.originalAmount ?? row.amount) - row.amount);
						barPercent = Math.round((savings / basePrice) * 100);
					} else {
						barPercent = 0;
					}
					const inner =
						row.type === "product-price" ? (
							<ProductPriceRow row={row} tab={activeTab} />
						) : row.type === "total" ? (
							<TotalRow row={row} />
						) : (
							<DiscountRow row={row} />
						);
					const isGreenRow = row.type !== "product-price";
					return (
						<div
							key={idx}
							className={`relative w-full rounded-[6px] border border-[var(--automint-onboarding-dotted-line)] bg-white overflow-hidden min-h-[70px] md:min-h-[62px] min-[1500px]:min-h-[70px]${isGreenRow ? " flex flex-col" : ""}`}
						>
							{isGreenRow && (
								<div
									className="absolute right-0 top-0 bottom-0 z-0"
									style={{
										width: `${barPercent}%`,
										background: "var(--automint-bar-gradient)",
									}}
									aria-hidden="true"
								/>
							)}
							{inner}
						</div>
					);
				})}
			</div>
			<p className="text-medium-gray font-light text-[11px] md:text-sm text-second mt-3">
				{activeTab.disclaimer ?? data.disclaimer}
			</p>
			{("gstNote" in activeTab ? activeTab.gstNote : data.gstNote) && (
				<p className="text-medium-gray text-[11px] md:text-sm font-light mt-3 md:mt-1">
					{"gstNote" in activeTab ? activeTab.gstNote : data.gstNote}
				</p>
			)}
		</div>
	);
};
